import {
  Button,
  colors,
  containerNames,
  containers,
  ControlGroup,
  darkThemeSelector,
  selectors,
  shadows,
  sizing,
  styled,
  zIndexes,
} from '@meterup/atto';

const flexiColumnsZIndexes = {
  background: zIndexes.base + 1,
  foreground: zIndexes.base + 2,
  peek: zIndexes.base + 3,
  divider: zIndexes.base + 4,
};

export const FLEXI_COLUMNS_ANIMATION_MS = 200;

export const FLEXI_COLUMNS_RESIZE_BOUNDS = {
  min: 280,
  max: 720,
};

const flexiColumnsToggleButtonStyles = {
  minWidth: 0,
};

export const FlexiColumnsToggleButton = styled(Button, {
  position: 'absolute',
  top: '$8',

  [containers.pane.notNarrow]: {
    transform: 'translateX(-50%)',
  },

  variants: {
    position: {
      start: {
        [containers.pane.narrow]: {
          left: '-$4',
          borderRadius: '0 $8 $8 0',
        },

        [containers.pane.notNarrow]: {
          left: `$${sizing.primary}`,
          transform: 'translateX(-50%)',
        },
      },
      end: {
        [containers.pane.narrow]: {
          right: '-$4',
          borderRadius: '$8 0 0 $8',
        },

        [containers.pane.notNarrow]: {
          right: `$${sizing.primary}`,
          transform: 'translateX(50%)',
        },
      },
    },
  },

  compoundVariants: [
    {
      position: 'start',
      css: { ...flexiColumnsToggleButtonStyles },
    },
    {
      position: 'end',
      css: { ...flexiColumnsToggleButtonStyles },
    },
  ],
});
FlexiColumnsToggleButton.displayName = 'FlexiColumnsToggleButton';

const columnVisibleStyles = {
  zIndex: 2,

  [containers.pane.narrow]: {
    overflow: 'auto',
    transform: 'translateX(0)',
  },

  [containers.pane.notNarrow]: {
    minWidth: 0,
    overflow: 'auto',
  },
};

const columnHiddenStyles = {
  zIndex: flexiColumnsZIndexes.peek,

  [containers.pane.narrow]: {
    overflow: 'visible',
    boxShadow: shadows.overlayLight,
    transform: `translateX(calc(-100% + ${sizing.primary}px))`,

    [darkThemeSelector]: {
      boxShadow: shadows.overlayDark,
    },

    [selectors.focus]: {
      outline: 'none',
    },
  },

  [containers.pane.notNarrow]: {
    overflow: 'hidden',
    minWidth: 0,
  },
};

export const FlexiColumnsColumn = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'auto',
  background: colors.bgApplicationLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
  },

  [containers.pane.narrow]: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    transition: `transform ${FLEXI_COLUMNS_ANIMATION_MS}ms ease`,
    willChange: 'transform',
  },

  variants: {
    hasDivider: {
      true: {},
      false: {},
    },
    position: {
      start: {
        [containers.pane.notNarrow]: {
          gridArea: 'start',
          strokeRight: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            strokeRight: colors.strokeNeutralDark,
          },
        },

        [containers.pane.narrow]: {
          zIndex: flexiColumnsZIndexes.foreground,
        },
      },
      end: {
        [containers.pane.notNarrow]: {
          gridArea: 'end',
          strokeLeft: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            strokeLeft: colors.strokeNeutralDark,
          },
        },

        [containers.pane.narrow]: {
          zIndex: flexiColumnsZIndexes.background,
        },
      },
    },
    visible: {
      true: columnVisibleStyles,
      false: columnHiddenStyles,
    },
  },

  compoundVariants: [
    {
      position: 'start',
      visible: false,
      css: {
        [containers.pane.narrow]: {
          transform: 'translateX(-100%)',
        },
        [containers.pane.notNarrow]: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        },
      },
    },
    {
      position: 'end',
      visible: false,
      css: {
        [containers.pane.narrow]: {
          transform: 'translateX(100%)',
        },
      },
    },
    {
      hasDivider: true,
      position: 'start',
      css: {
        [containers.pane.notNarrow]: {
          strokeRight: colors.transparent,

          [darkThemeSelector]: {
            strokeRight: colors.transparent,
          },
        },
      },
    },
    {
      position: 'end',
      visible: false,
      css: {
        [containers.pane.notNarrow]: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        },
      },
    },
    {
      hasDivider: true,
      position: 'end',
      css: {
        [containers.pane.notNarrow]: {
          strokeLeft: colors.transparent,

          [darkThemeSelector]: {
            strokeLeft: colors.transparent,
          },
        },
      },
    },
  ],
});
FlexiColumnsColumn.displayName = 'FlexiColumnsColumn';

export const FlexiColumnsColumnBody = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: 1,
});
FlexiColumnsColumnBody.displayName = 'FlexiColumnsColumnBody';

const DIVIDER_WIDTH = '$1';

export const FlexiColumnsContainer = styled('div', {
  position: 'relative',
  width: '100%',
  height: '100%',
  minHeight: '0',
  containerType: 'inline-size',
  containerName: containerNames.pane,

  [containers.pane.narrow]: {
    overflowX: 'hidden',
    gridTemplateColumns: '1fr',
    gridTemplateAreas: '"start"',

    '&[data-active-column="start"] > [data-position="start"]': {
      zIndex: 2,
      overflow: 'auto',
      transform: 'translateX(0)',
    },
    '&[data-active-column="start"] > [data-position="end"]': {
      zIndex: flexiColumnsZIndexes.background,
      overflow: 'visible',
      transform: 'translateX(100%)',
    },
    '&[data-active-column="end"] > [data-position="end"]': {
      zIndex: 2,
      overflow: 'auto',
      transform: 'translateX(0)',
    },
    '&[data-active-column="end"] > [data-position="start"]': {
      zIndex: flexiColumnsZIndexes.foreground,
      overflow: 'visible',
      transform: 'translateX(-100%)',
    },

    '& [data-flexi-overlay]': {
      display: 'block',
    },
    '& [data-flexi-divider]': {
      display: 'none',
    },
  },

  [containers.pane.notNarrow]: {
    display: 'grid',
    gridTemplateRows: '1fr',
  },

  '&[data-both-panes-visible="true"][data-resizable="true"]': {
    [containers.pane.notNarrow]: {
      gridTemplateAreas: '"start divider end"',

      '&[data-preset="narrow-wide"]': {
        gridTemplateColumns: `var(--flexi-start-width) ${DIVIDER_WIDTH} 1fr`,
      },
      '&[data-preset="wide-narrow"]': {
        gridTemplateColumns: `1fr ${DIVIDER_WIDTH} var(--flexi-start-width)`,
      },
      '&[data-preset="even-split"]': {
        gridTemplateColumns: `var(--flexi-start-fr) ${DIVIDER_WIDTH} var(--flexi-end-fr)`,
      },
    },
  },

  '&[data-both-panes-visible="true"][data-resizable="false"]': {
    [containers.pane.notNarrow]: {
      gridTemplateAreas: '"start end"',

      '&[data-preset="narrow-wide"]': {
        gridTemplateColumns: 'var(--flexi-fixed-start, $400) 1fr',
      },
      '&[data-preset="wide-narrow"]': {
        gridTemplateColumns: '1fr var(--flexi-fixed-end, $400)',
      },
      '&[data-preset="even-split"]': {
        gridTemplateColumns: '1fr 1fr',
      },
    },
  },

  '&[data-both-panes-visible="false"]': {
    [containers.pane.notNarrow]: {
      gridTemplateAreas: '"start end"',

      '&[data-active-column="start"]': {
        gridTemplateColumns: '1fr var(--flexi-peek-width-end)',
      },
      '&[data-active-column="end"]': {
        gridTemplateColumns: 'var(--flexi-peek-width-start) 1fr',
      },

      '&[data-active-column="end"] > [data-position="start"] [data-flexi-slot="body"]': {
        width: 'max-content',
        minWidth: '100%',
      },
      '&[data-active-column="start"] > [data-position="end"] [data-flexi-slot="body"]': {
        width: 'max-content',
        minWidth: '100%',
      },

      '& [data-flexi-overlay]': {
        display: 'block',
      },
      '& [data-flexi-divider]': {
        display: 'none',
      },
    },
  },

  '&[data-both-panes-visible="true"]': {
    [containers.pane.notNarrow]: {
      '& [data-flexi-overlay]': {
        display: 'none',
      },
    },
  },

  '&[data-active-column="start"] > [data-flexi-overlay] [data-toggle="start"]': {
    display: 'none',
  },
  '&[data-active-column="end"] > [data-flexi-overlay] [data-toggle="end"]': {
    display: 'none',
  },
  '&[data-nested-active-column="end"] > [data-flexi-overlay] [data-toggle="start"]': {
    [containers.pane.narrow]: {
      display: 'none',
    },
  },
});
FlexiColumnsContainer.displayName = 'FlexiColumnsContainer';

export const FlexiColumnsOverlay = styled('div', {
  position: 'absolute',
  inset: 0,
  zIndex: flexiColumnsZIndexes.peek,
  pointerEvents: 'none',
  display: 'none',

  '& > *': {
    pointerEvents: 'auto',
  },
});
FlexiColumnsOverlay.displayName = 'FlexiColumnsOverlay';

export const FlexiColumnsControlsContainer = styled(ControlGroup, {
  position: 'absolute',
  top: '$10',
  left: 0,
  transform: 'translateX(-50%)',
});
FlexiColumnsControlsContainer.displayName = 'FlexiColumnsControlsContainer';

export const FlexiColumnsDivider = styled('div', {
  gridArea: 'divider',
  position: 'relative',
  widthAll: DIVIDER_WIDTH,
  zIndex: flexiColumnsZIndexes.divider,
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    backgroundColor: colors.strokeNeutralDark,
  },
});
FlexiColumnsDivider.displayName = 'FlexiColumnsDivider';
